import { Controller } from "@hotwired/stimulus"

// Allows users to accumulate multiple files from different locations
// before submitting a form. Files can be added incrementally and removed individually.
// Each file has its own description field.
export default class extends Controller {
  static targets = ["input", "list", "count"]
  
  connect() {
    // DataTransfer lets us manipulate FileList
    this.files = new DataTransfer()
    // Track descriptions per file (by filename since indices can shift)
    this.descriptions = new Map()
    this.updateDisplay()
  }
  
  disconnect() {
    // Clean up when controller disconnects (Turbo navigation, etc.)
    this.files = null
    this.descriptions = null
  }
  
  // Called when user selects file(s) via the visible input
  add(event) {
    const newFiles = event.target.files
    
    for (let i = 0; i < newFiles.length; i++) {
      // Avoid duplicates by filename
      const exists = Array.from(this.files.files).some(f => f.name === newFiles[i].name)
      if (!exists) {
        this.files.items.add(newFiles[i])
        // Initialize empty description for new file
        this.descriptions.set(newFiles[i].name, "")
      }
    }
    
    // Update the hidden input that holds all accumulated files
    this.inputTarget.files = this.files.files
    
    // Clear the visible input so user can add more
    event.target.value = ""
    
    this.updateDisplay()
  }
  
  // Remove a file by index
  remove(event) {
    const index = parseInt(event.params.index)
    const fileArray = Array.from(this.files.files)
    const removedFile = fileArray[index]
    
    // Remove description for this file
    if (removedFile) {
      this.descriptions.delete(removedFile.name)
    }
    
    const newFiles = new DataTransfer()
    fileArray.forEach((file, i) => {
      if (i !== index) {
        newFiles.items.add(file)
      }
    })
    
    this.files = newFiles
    this.inputTarget.files = this.files.files
    this.updateDisplay()
  }
  
  // Update description for a file
  updateDescription(event) {
    const filename = event.params.filename
    const description = event.target.value
    this.descriptions.set(filename, description)
  }
  
  // Clear all files
  clear() {
    this.files = new DataTransfer()
    this.descriptions = new Map()
    this.inputTarget.files = this.files.files
    this.updateDisplay()
  }
  
  updateDisplay() {
    const fileArray = Array.from(this.files.files)
    
    // Update count
    if (this.hasCountTarget) {
      this.countTarget.textContent = fileArray.length > 0 
        ? `${fileArray.length} file(s) ready to upload` 
        : ""
    }
    
    // Update list
    if (this.hasListTarget) {
      if (fileArray.length === 0) {
        this.listTarget.innerHTML = ""
        return
      }
      
      this.listTarget.innerHTML = fileArray.map((file, index) => {
        const currentDesc = this.descriptions.get(file.name) || ""
        return `
        <div class="bg-white rounded mb-2 border p-2">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="small text-truncate me-2">
              <i class="bi bi-file-earmark me-1"></i>
              ${this.escapeHtml(file.name)}
              <span class="text-muted">(${this.formatSize(file.size)})</span>
            </span>
            <button type="button" 
                    class="btn btn-sm btn-link text-danger p-0" 
                    data-action="file-accumulator#remove"
                    data-file-accumulator-index-param="${index}"
                    aria-label="Remove ${this.escapeHtml(file.name)}">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <input type="text" 
                 name="file_descriptions[]" 
                 class="form-control form-control-sm" 
                 placeholder="What does this file contain?"
                 value="${this.escapeHtml(currentDesc)}"
                 data-action="input->file-accumulator#updateDescription"
                 data-file-accumulator-filename-param="${this.escapeHtml(file.name)}"
                 required>
        </div>
      `}).join("")
    }
  }
  
  formatSize(bytes) {
    if (bytes === 0) return "0 B"
    const k = 1024
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
  }
  
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
